import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Theme system — Phase 28.6.
 *
 * Three values:
 *   • "auto"  → follow `prefers-color-scheme` (and the existing mobile-only
 *               auto-light rules in index.css). This is the default and
 *               preserves the historical behaviour byte-for-byte.
 *   • "light" → force light. Adds `data-theme="light"` on <html>; matching
 *               CSS in index.css applies the light overrides on all viewports.
 *   • "dark"  → force dark. Adds `data-theme="dark"` on <html>; the auto
 *               light rules are gated behind `:root:not([data-theme])` so
 *               they no longer apply.
 *
 * Persistence: `localStorage["sdrive_theme"]`. Read on mount, written on
 * every change. Pure frontend — zero backend impact.
 */

const STORAGE_KEY = "sdrive_theme";
const VALID = ["auto", "light", "dark"];

function readPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "auto";
  } catch {
    return "auto";
  }
}

function applyToDocument(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "auto") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
}

const ThemeCtx = createContext({ theme: "auto", setTheme: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readPreference());

  // Apply on every change (and once on mount).
  useEffect(() => { applyToDocument(theme); }, [theme]);

  const setTheme = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  return useContext(ThemeCtx);
}
